import { AvailableOrderTypesEnum } from 'src/components/enterprise/features/OrderHistory/OrderList/TableRow';
import { mapOrderToChargesProps } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/mapOrderToChargesProps';
import {
  IAllocationProps,
  ICorpDataProps,
  IOrderReceiptProps,
} from 'src/components/enterprise/features/OrderHistory/OrderReceipt/OrderReceipt';
import {
  IAllocationDetails,
  IDeliveryInfo,
  IOrdersReceipt,
} from 'src/types/OrderHistory';
import { getFormattedTimezoneFromObject } from 'src/utilities/time-utils';
import { IFulfillmentInfoProps } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/FulfillmentInfo';
import { FulfillmentType } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';
import { IDeliveryInfoProps } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/FulfillmentInfo/DeliveryInfo';
import { IPickupInfoProps } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/FulfillmentInfo/PickupInfo';
import { createItems, createParticipantLines } from 'src/common/Order/utils'; // here
import { formatPenniesToCurrency } from 'src/common/utils/formatPenniesToCurrency';

const DATE_FORMAT = 'MMM d, yyyy h:mma';

export function mapOrderToReceiptProps(
  order: IOrdersReceipt,
  corpData: ICorpDataProps
): IOrderReceiptProps {
  const {
    meal_type: mealType,
    ordered_from: { restaurant_name: restaurantName },
    ordered_by: { name: dinerName, email, expense_code: expenseCode },
    time_placed_utc: timePlaced,
    delivered_on_utc: fulfilledOn,
    time_zone: timeZone,
    delivery_info: deliveryInfo,
    expense_comments: expenseComments,
    order_number: orderNumber,
    line_items: lineItems,
    group_order: isGroupOrder,
    group_order_info: groupOrderInfo,
    group_admin: isGroupAdmin,
  } = order;

  const { delivery_instructions: deliveryInstructions } = deliveryInfo;

  const fulfillmentDate = getFormattedTimezoneFromObject(
    fulfilledOn,
    timeZone,
    DATE_FORMAT
  );

  return {
    dinerName,
    email,
    corpData,
    timePlaced: getFormattedTimezoneFromObject(
      timePlaced,
      timeZone,
      DATE_FORMAT
    ),
    fulfillmentInfo: getFulfillmentInfo(deliveryInfo),
    orderType: AvailableOrderTypesEnum[mealType],
    restaurantName,
    orderNumber,
    expenseCode,
    expenseComments,
    items: createItems(lineItems),
    charges: mapOrderToChargesProps(order),
    allocations: getAllocations(order.allocation_details),
    transactionFee: formatPenniesToCurrency(order.transaction_fee),
    total: formatPenniesToCurrency(order.total_expense),
    isGroupOrder,
    isGroupAdmin,
    participantLines: createParticipantLines(isGroupOrder, groupOrderInfo),
  };

  function getFulfillmentInfo(
    deliveryInfo: IDeliveryInfo
  ): IFulfillmentInfoProps {
    if (isDeliveryOrder(deliveryInfo)) return getDeliveryInfo(deliveryInfo);

    return getPickInfo();
  }

  function isDeliveryOrder(deliveryInfo: IDeliveryInfo) {
    const {
      street_address1,
      street_address2,
      address_locality,
      address_region,
      postal_code,
    } = deliveryInfo;

    return [
      street_address1,
      street_address2,
      address_locality,
      address_region,
      postal_code,
    ].some((value) => !!value);
  }

  function getDeliveryInfo(deliveryInfo: IDeliveryInfo): IDeliveryInfoProps {
    return {
      type: FulfillmentType.DELIVERY,
      date: fulfillmentDate,
      address: getAddress(deliveryInfo),
      instructions: deliveryInstructions,
    };
  }

  function getPickInfo(): IPickupInfoProps {
    return {
      type: FulfillmentType.PICKUP,
      date: fulfillmentDate,
    };
  }

  function getAddress(deliveryInfo: IDeliveryInfo) {
    const {
      street_address1: streetAddress1,
      street_address2: streetAddress2,
      address_locality: addressLocality,
      address_region: addressRegion,
      postal_code: postalCode,
    } = deliveryInfo;

    return [
      streetAddress1,
      streetAddress2,
      addressLocality,
      addressRegion,
      postalCode,
    ]
      .filter((value) => !!value)
      .join(', ');
  }

  function getAllocations(order: IAllocationDetails[]): IAllocationProps[] {
    return order.map((allocation) => ({
      name: allocation.name,
      amount: formatPenniesToCurrency(allocation.amount),
    }));
  }
}
